/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnDestroy, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { ContentNodeSelectorComponent, ContentNodeSelectorComponentData, NodeAction } from '@alfresco/adf-content-services';
import { Node } from '@alfresco/js-api';
import { Subject } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConnectorRegistry } from './connector-registry.service';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import {
  ConnectorCategory,
  DuplicatePolicy,
  MappingRow,
  MigrationConnection,
  MigrationConnector,
  MigrationJob,
  MigrationStatus,
  DEFAULT_MIGRATION_SCHEDULE,
  emptyMapping,
  emptySelection
} from './migration.models';
import { ConnectionDialogComponent } from './connection-dialog.component';

/**
 * The New Migration wizard — ten steps, each gated on what it can honestly
 * establish. Steps that need the migration engine (discovery, dry run, run,
 * verify) ask it for the real answer: discovery scans the connection, the
 * select step browses its tree, dry run rehearses the job, verify polls the
 * live run. Without the engine each step explains what is missing and the
 * job stays a draft. A draft may be saved at any point; "Start migration"
 * is disabled until the engine exists and validation passed.
 */
@Component({
  selector: 'app-mig-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatDialogModule, MatTooltipModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-wizard.component.html'
})
export class MigrationWizardComponent implements OnInit, OnDestroy {
  private readonly service = inject(MigrationService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  readonly registry = inject(ConnectorRegistry);
  readonly api = inject(MigrationApiService);

  readonly step = signal(0);
  readonly busy = signal(false);
  readonly connections = signal<MigrationConnection[]>([]);
  readonly categories = this.registry.categories;

  /** Engine answers — null until asked, {failed:true} when the call failed. */
  readonly discovering = signal(false);
  readonly discovery = signal<any>(null);
  readonly browsing = signal(false);
  readonly browsePath = signal('');
  readonly browseEntries = signal<any[]>([]);
  readonly browseFailed = signal(false);
  readonly dryRunning = signal(false);
  readonly dryResult = signal<any>(null);
  readonly liveState = signal<any>(null);
  private liveTimer: ReturnType<typeof setInterval> | null = null;

  /** The draft being built — the real object that gets persisted. */
  job: MigrationJob = this.blankJob();

  /** New row being typed into a manual source-path / mapping list. */
  newPath = '';
  newMapSource = '';
  newMapTarget = '';
  mapTable: 'folders' | 'types' | 'metadata' | 'users' | 'permissions' = 'metadata';

  readonly stepKeys = [
    'DMS.MIGRATION.W_SOURCE',
    'DMS.MIGRATION.W_CONNECTION',
    'DMS.MIGRATION.W_DISCOVERY',
    'DMS.MIGRATION.W_SELECT',
    'DMS.MIGRATION.W_MAPPING',
    'DMS.MIGRATION.W_VALIDATION',
    'DMS.MIGRATION.W_DRYRUN',
    'DMS.MIGRATION.W_MIGRATION',
    'DMS.MIGRATION.W_VERIFY',
    'DMS.MIGRATION.W_COMPLETE'
  ];

  ngOnInit(): void {
    this.service.connections().subscribe((list) => this.connections.set(list));
    const draft = this.service.pendingDraft();
    if (draft) {
      this.service.pendingDraft.set(null);
      this.job = {
        ...draft,
        selection: { ...emptySelection(), ...draft.selection },
        mapping: { ...emptyMapping(), ...draft.mapping },
        schedule: { ...DEFAULT_MIGRATION_SCHEDULE, ...draft.schedule }
      };
      this.step.set(Math.min(9, Math.max(0, draft.currentStep ?? 0)));
      this.enterStep();
      return;
    }
    // saved deployment settings are the defaults a fresh draft starts from —
    // the engine reads the same file for its run-time knobs
    this.service.settings().subscribe((s) => {
      this.job.duplicatePolicy = s.duplicatePolicy ?? this.job.duplicatePolicy;
      this.job.checksumVerification = s.checksumVerification ?? this.job.checksumVerification;
      this.job.selection.includeVersions = s.preserveVersions ?? this.job.selection.includeVersions;
      this.job.selection.includePermissions = s.preservePermissions ?? this.job.selection.includePermissions;
    });
  }

  ngOnDestroy(): void {
    this.stopLivePolling();
  }

  private blankJob(): MigrationJob {
    return {
      name: '',
      sourceConnector: '',
      destination: 'current',
      destinationPath: '',
      mode: 'standard',
      status: 'draft',
      currentStep: 0,
      selection: emptySelection(),
      mapping: emptyMapping(),
      duplicatePolicy: 'skip',
      checksumVerification: true,
      schedule: { ...DEFAULT_MIGRATION_SCHEDULE },
      createdBy: '',
      createdAt: ''
    };
  }

  // ---- source step -------------------------------------------------------

  connectorsOf(category: ConnectorCategory): MigrationConnector[] {
    return this.registry.byCategory(category);
  }

  get connector(): MigrationConnector | undefined {
    return this.registry.get(this.job.sourceConnector);
  }

  /** UI-selectable but missing from the running engine's implemented list. */
  engineUnsupported(c: MigrationConnector): boolean {
    return c.availability === 'available' && this.api.available() === true && !this.api.engineSupports(c.id);
  }

  pickConnector(c: MigrationConnector): void {
    if (c.availability !== 'available') {
      return;
    }
    this.job.sourceConnector = c.id;
    // a connector change invalidates a connection picked for another type
    if (this.job.sourceConnectionId && !this.connections().some((x) => x.id === this.job.sourceConnectionId && x.connectorId === c.id)) {
      this.job.sourceConnectionId = undefined;
      this.resetEngineState();
    }
  }

  /** Changing the connection changes which source every engine answer describes. */
  onConnectionChange(): void {
    this.resetEngineState();
  }

  private resetEngineState(): void {
    this.discovery.set(null);
    this.browsePath.set('');
    this.browseEntries.set([]);
    this.browseFailed.set(false);
    this.dryResult.set(null);
  }

  connectionsForConnector(): MigrationConnection[] {
    return this.connections().filter((c) => c.connectorId === this.job.sourceConnector);
  }

  addConnection(): void {
    this.dialog
      .open(ConnectionDialogComponent, { width: '560px', maxWidth: '96vw', autoFocus: false, data: { connectorId: this.job.sourceConnector } })
      .afterClosed()
      .subscribe((saved) => {
        if (!saved) {
          return;
        }
        this.service.connections().subscribe((list) => {
          this.connections.set(list);
          // the dialog locked the connector to this job's source, so the
          // connection it just saved is the one this step is asking for —
          // pick it rather than making the person open the dropdown again
          const created = typeof saved === 'object' ? (saved as MigrationConnection) : undefined;
          if (created?.connectorId === this.job.sourceConnector) {
            this.job.sourceConnectionId = created.id;
          }
        });
      });
  }

  // ---- select data --------------------------------------------------------

  addPath(): void {
    const p = this.newPath.trim();
    if (p && !this.job.selection.paths.includes(p)) {
      this.job.selection.paths = [...this.job.selection.paths, p];
      this.dryResult.set(null); // the rehearsal no longer matches the selection
    }
    this.newPath = '';
  }

  removePath(p: string): void {
    this.job.selection.paths = this.job.selection.paths.filter((x) => x !== p);
    this.dryResult.set(null);
  }

  // ---- mapping ------------------------------------------------------------

  addMappingRow(): void {
    const source = this.newMapSource.trim();
    const target = this.newMapTarget.trim();
    if (!source || !target) {
      return;
    }
    const row: MappingRow = { source, target, action: 'map' };
    this.job.mapping[this.mapTable] = [...this.job.mapping[this.mapTable], row];
    this.newMapSource = '';
    this.newMapTarget = '';
  }

  removeMappingRow(table: 'folders' | 'types' | 'metadata' | 'users' | 'permissions', index: number): void {
    this.job.mapping[table] = this.job.mapping[table].filter((_, i) => i !== index);
  }

  setRowAction(row: MappingRow, action: string): void {
    row.action = action;
  }

  // ---- engine-backed steps ------------------------------------------------

  /** Discovery — the engine scans the saved connection and reports what is there. */
  runDiscovery(): void {
    const connId = this.job.sourceConnectionId;
    this.discovery.set(null);
    if (!connId || this.api.available() !== true) {
      return;
    }
    this.discovering.set(true);
    this.api.discover(connId).subscribe({
      next: (res) => {
        this.discovering.set(false);
        this.discovery.set(res);
      },
      error: (e) => {
        this.discovering.set(false);
        this.discovery.set({ failed: true, detail: e?.error?.detail });
      }
    });
  }

  /** One level of the source tree — empty path asks for the connector's root. */
  browse(path: string): void {
    const connId = this.job.sourceConnectionId;
    if (!connId || this.api.available() !== true) {
      return;
    }
    this.browsing.set(true);
    this.browseFailed.set(false);
    this.api.browse(connId, path).subscribe({
      next: (res) => {
        this.browsing.set(false);
        this.browsePath.set(res?.path ?? path);
        this.browseEntries.set(Array.isArray(res?.entries) ? res.entries : []);
      },
      error: () => {
        this.browsing.set(false);
        this.browseFailed.set(true);
        this.browseEntries.set([]);
      }
    });
  }

  browseUp(): void {
    const parent = this.browsePath().replace(/\/+$/, '');
    const i = parent.lastIndexOf('/');
    this.browse(i > 0 ? parent.slice(0, i) : '');
  }

  addBrowsePath(entry: { path: string }): void {
    if (entry.path && !this.job.selection.paths.includes(entry.path)) {
      this.job.selection.paths = [...this.job.selection.paths, entry.path];
      this.dryResult.set(null); // the rehearsal no longer matches the selection
    }
  }

  /** Dry run — the engine rehearses this draft: enumeration + duplicate analysis, no writes. */
  runDryRun(): void {
    if (this.api.available() !== true || !this.job.selection.paths.length || this.dryResult()) {
      return;
    }
    this.dryRunning.set(true);
    this.api.dryRun(this.job).subscribe({
      next: (res) => {
        this.dryRunning.set(false);
        this.dryResult.set(res);
      },
      error: (e) => {
        this.dryRunning.set(false);
        this.dryResult.set({ failed: true, detail: e?.error?.detail });
      }
    });
  }

  rerunDryRun(): void {
    this.dryResult.set(null);
    this.runDryRun();
  }

  // ---- live run (verification step) ---------------------------------------

  /**
   * Polls the engine's live state while the run is in flight. A 404 means the
   * engine no longer tracks it (finished before the first poll, or the service
   * restarted) — then the job file's own write-back has the final word.
   */
  private startLivePolling(): void {
    this.stopLivePolling();
    this.liveState.set(null);
    if (!this.job.id || this.api.available() !== true) {
      return;
    }
    const tick = () => {
      const id = this.job.id;
      if (!id) {
        this.stopLivePolling();
        return;
      }
      this.api.jobState(id).subscribe((state) => {
        if (state) {
          this.liveState.set(state);
          if (state.status) {
            this.job.status = state.status as MigrationStatus;
          }
          if (state.statistics) {
            this.job.statistics = state.statistics;
          }
          if (state.status !== 'running' && state.status !== 'paused') {
            this.stopLivePolling();
          }
          return;
        }
        this.service.jobs().subscribe((list) => {
          const fresh = list.find((j) => j.id === id);
          if (fresh) {
            this.job.status = fresh.status;
            this.job.statistics = fresh.statistics;
            this.job.completedAt = fresh.completedAt;
          }
          this.stopLivePolling();
        });
      });
    };
    tick();
    this.liveTimer = setInterval(tick, 4000);
  }

  private stopLivePolling(): void {
    if (this.liveTimer !== null) {
      clearInterval(this.liveTimer);
      this.liveTimer = null;
    }
  }

  /** What a step needs when it becomes visible — engine answers load here, not upfront. */
  private enterStep(): void {
    switch (this.step()) {
      case 2:
        this.runDiscovery();
        break;
      case 3:
        if (this.api.available() === true && this.job.sourceConnectionId && !this.browseEntries().length && !this.browseFailed()) {
          this.browse(this.browsePath());
        }
        break;
      case 6:
        this.runDryRun();
        break;
      case 8:
        this.startLivePolling();
        break;
      default:
        this.stopLivePolling();
    }
  }

  // ---- validation (the honest, client-side kind) --------------------------

  /**
   * What can be checked without the engine: the draft is complete enough to
   * describe a migration. Engine checks (connectivity, duplicates, size) are
   * reported separately as "not verified".
   */
  validationResults(): { level: 'pass' | 'warn' | 'error'; labelKey: string }[] {
    const out: { level: 'pass' | 'warn' | 'error'; labelKey: string }[] = [];
    const push = (ok: boolean, labelKey: string, warn = false) => out.push({ level: ok ? 'pass' : warn ? 'warn' : 'error', labelKey });

    push(!!this.job.sourceConnector, 'DMS.MIGRATION.V_SOURCE');
    push(!!this.job.name.trim(), 'DMS.MIGRATION.V_NAME');
    push(this.job.selection.paths.length > 0, 'DMS.MIGRATION.V_SELECTION');
    push(!!this.job.sourceConnectionId, 'DMS.MIGRATION.V_CONNECTION', true);
    push(!!this.job.destinationPath?.trim(), 'DMS.MIGRATION.V_DESTPATH', true);
    push(this.job.duplicatePolicy !== 'replace', 'DMS.MIGRATION.V_DUPPOLICY', true);
    return out;
  }

  hasBlockingError(): boolean {
    return this.validationResults().some((r) => r.level === 'error');
  }

  // ---- step gating ----------------------------------------------------------

  /**
   * What "next" requires: the step's own inputs, not the engine's. Steps that
   * need the engine to prove anything display that state instead — the draft
   * can still be assembled past them; starting cannot.
   */
  canNext(): boolean {
    switch (this.step()) {
      case 0:
        return !!this.job.sourceConnector && !!this.job.name.trim();
      case 1:
        return true; // a connection may be chosen later — drafts allow it
      case 2:
        return true; // discovery needs the engine; the step explains that
      case 3:
        return this.job.selection.paths.length > 0;
      case 4:
        return true; // an empty mapping is a valid choice — fields pass through
      case 5:
        return !this.hasBlockingError();
      case 6:
        return true;
      case 7:
        return false; // starting is an engine action, not a wizard step-through
      case 8:
        return true;
      default:
        return false;
    }
  }

  next(): void {
    if (this.canNext() && this.step() < 9) {
      this.step.set(this.step() + 1);
      this.job.currentStep = this.step();
      this.enterStep();
    }
  }

  back(): void {
    if (this.step() > 0) {
      this.step.set(this.step() - 1);
      this.job.currentStep = this.step();
      this.enterStep();
    }
  }

  cancel(): void {
    this.job = this.blankJob();
    this.step.set(0);
  }

  /** Opens the repository folder picker — destinationPath is Company-Home-relative. */
  browseDestination(): void {
    const select = new Subject<Node[]>();
    const data: ContentNodeSelectorComponentData = {
      title: 'DMS.MIGRATION.BROWSE_DEST',
      actionName: NodeAction.CHOOSE,
      currentFolderId: '-root-',
      selectionMode: 'single',
      where: '(isFolder=true)',
      isSelectionValid: (node: Node) => node.isFolder,
      select
    };
    this.dialog.open(ContentNodeSelectorComponent, { data, panelClass: 'adf-content-node-selector-dialog', width: '630px' });
    select.subscribe((nodes) => {
      const folder = nodes?.[0];
      if (folder) {
        this.job.destinationPath = MigrationWizardComponent.nodePath(folder);
      }
    });
  }

  /** "Sites/x/documentLibrary/folder" — Company Home itself is dropped from the crumbs. */
  private static nodePath(node: Node): string {
    const crumbs = (node.path?.elements ?? []).map((e) => e.name).filter((name) => name !== 'Company Home');
    return [...crumbs, node.name].join('/');
  }

  // ---- draft / start --------------------------------------------------------

  saveDraft(): void {
    this.busy.set(true);
    this.service.saveJob({ ...this.job }).subscribe({
      next: (id) => {
        this.busy.set(false);
        this.job.id = id;
        this.notification.showInfo(this.translate.instant('DMS.MIGRATION.DRAFT_SAVED'));
      },
      error: () => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.MIGRATION.SAVE_FAIL'));
      }
    });
  }

  /** The destructive-ish confirmation before a real run — honest about scope. */
  startMigration(): void {
    if (!this.api.available() || this.hasBlockingError() || !this.job.id) {
      return;
    }
    const stats = this.job.statistics;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'DMS.MIGRATION.START_CONFIRM_TITLE',
          message: this.translate.instant('DMS.MIGRATION.START_CONFIRM_MSG', {
            name: this.job.name,
            source: this.connector ? this.translate.instant(this.connector.titleKey) : '—',
            docs: stats?.documents?.toLocaleString() ?? '—',
            policy: this.translate.instant(`DMS.MIGRATION.DUP_${this.job.duplicatePolicy.toUpperCase().replace('-', '_')}`)
          }),
          yesLabel: 'DMS.MIGRATION.START',
          noLabel: 'DMS.MIGRATION.CANCEL'
        },
        minWidth: '320px'
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true || !this.job.id) {
          return;
        }
        this.busy.set(true);
        this.api.start(this.job.id).subscribe({
          next: () => {
            this.busy.set(false);
            this.job.status = 'running';
            this.job.startedAt = new Date().toISOString();
            this.service.saveJob(this.job).subscribe({ error: () => undefined });
            this.service.audit('migration.started', { jobId: this.job.id, detail: this.job.name }).subscribe({ error: () => undefined });
            this.notification.showInfo(this.translate.instant('DMS.MIGRATION.STARTED'));
            // the run now exists — the verification step watches it live
            this.step.set(8);
            this.job.currentStep = 8;
            this.startLivePolling();
          },
          error: (e) => {
            this.busy.set(false);
            this.notification.showError(e?.error?.detail ?? this.translate.instant('DMS.MIGRATION.START_FAIL'));
          }
        });
      });
  }

  dupLabel(policy: DuplicatePolicy): string {
    return this.translate.instant(`DMS.MIGRATION.DUP_${policy.toUpperCase().replace('-', '_')}`);
  }

  statusKey(status: string): string {
    return `DMS.MIGRATION.ST_${(status || 'draft').toUpperCase().replace(/-/g, '_')}`;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'running':
        return 'aca-mig__chip--running';
      case 'completed':
        return 'aca-mig__chip--ok';
      case 'completed-warnings':
        return 'aca-mig__chip--warn';
      case 'failed':
      case 'cancelled':
      case 'interrupted':
        return 'aca-mig__chip--fail';
      default:
        return '';
    }
  }

  trackByStep(i: number): number {
    return i;
  }

  trackByConn(_i: number, c: MigrationConnector): string {
    return c.id;
  }

  trackByRow(i: number): number {
    return i;
  }
}
